import * as Api from '@ViewModels';
import { THEME } from '@Theme';
import { StyleSheet, css } from 'aphrodite';
import moment from 'moment';
import { Pill } from '../../../../web/components-v2/Pill/Pill';
import { isPendingCancellation } from '../accountOverviewFormatters';

/** Pill `label`-variant colors used by the status chips. */
type ChipColor = 'green' | 'red' | 'orange';

const styles = StyleSheet.create({
	row: {
		display: 'flex',
		flexWrap: 'wrap',
		gap: THEME.spacing.sm,
	},
});

/**
 * Color for a no-show chip (Success / Ads / Web), ported from the legacy `renderAccountStatusRow`:
 * not a no-show is healthy (green); a no-show with a future meeting set is a soft warning (orange);
 * a no-show with nothing on the books is the worst case (red).
 */
const noShowColor = (noShow: boolean, hasFutureNext: boolean): ChipColor =>
	!noShow ? 'green' : hasFutureNext ? 'orange' : 'red';

/** True only when the account was created within the last 90 days (and a creation date exists). */
const isNewAccount = (account: Api.IAccount): boolean =>
	!!account.creationDate && moment().diff(moment(account.creationDate), 'days') <= 90;

interface IStatusChip {
	label: string;
	color: ChipColor;
}

/**
 * Derives the account-status chips purely from the persisted account (no viewmodel). Mirrors the
 * legacy `renderAccountStatusRow`: the Success No-Show, Coffee Target, and Next Meeting chips
 * always render; the Ads and Web No-Show chips render only when their feature is enabled; the
 * New Account chip renders only inside the 90-day creation window.
 */
const deriveStatusChips = (account: Api.IAccount): IStatusChip[] => {
	const additionalInfo = account.additionalInfo;
	const health = additionalInfo?.health;
	const chips: IStatusChip[] = [];

	if (isPendingCancellation(account)) {
		chips.push({ label: 'Pending Cancel', color: 'red' });
	}

	const successNoShow = !!health?.lastCsmMeetingNoShow;
	chips.push({
		label: `Success No Show: ${successNoShow ? 'Yes' : 'No'}`,
		color: noShowColor(successNoShow, !!additionalInfo?.nextMeeting),
	});

	if (account.features?.ads?.enabled) {
		const adsNoShow = !!health?.lastAdsMeetingNoShow;
		chips.push({
			label: `Ads No Show: ${adsNoShow ? 'Yes' : 'No'}`,
			color: noShowColor(adsNoShow, !!additionalInfo?.nextAdsMeeting),
		});
	}

	if (account.features?.websites?.enabled) {
		const webNoShow = !!health?.lastWebMeetingNoShow;
		chips.push({
			label: `Web No Show: ${webNoShow ? 'Yes' : 'No'}`,
			color: noShowColor(webNoShow, !!additionalInfo?.nextWebsitesMeeting),
		});
	}

	const isCoffeeTarget = !!health?.isCoffeeTarget;
	chips.push({ label: `Coffee Target: ${isCoffeeTarget ? 'Yes' : 'No'}`, color: isCoffeeTarget ? 'red' : 'green' });

	const hasNextMeeting = !!health?.hasNextMeetingBooked;
	chips.push({ label: `Next Meeting: ${hasNextMeeting ? 'Yes' : 'No'}`, color: hasNextMeeting ? 'green' : 'red' });

	if (isNewAccount(account)) {
		chips.push({ label: 'New Account', color: 'green' });
	}

	return chips;
};

export interface IAccountStatusChipsProps {
	/** The persisted account; chip presence/labels/colors are derived purely from it. */
	account: Api.IAccount;
}

/**
 * Account Overview status-chip row — a wrapping row of components-v2 `Pill`s summarizing the
 * account's success/ads/web no-show health, coffee-target / next-meeting flags, and new-account
 * status. Pure derivation off the react-query `account`; no MobX/viewmodel.
 */
export const AccountStatusChips = ({ account }: IAccountStatusChipsProps) => {
	const chips = deriveStatusChips(account);

	return (
		<div className={css(styles.row)}>
			{chips.map(chip => (
				<Pill key={chip.label} variant='label' color={chip.color} label={chip.label} />
			))}
		</div>
	);
};
